/*
 * HVACloud AI · Ein HMI-Bild vorschlagen lassen (Konzept J.6).
 *
 * Derselbe Ablauf wie in `AiDashboardModal`: Hinweis eingeben, laufen lassen,
 * Vorschau ansehen, uebernehmen. Zwei Dinge sind hier anders, und beide aus
 * demselben Grund.
 *
 * DIE VORSCHAU IST DAS BILD UND KEINE LISTE. Ein Dashboard-Vorschlag laesst
 * sich als Aufzaehlung von Seiten und Widgets beurteilen; ein HMI-Bild nicht.
 * Bei einem HMI-Bild ist die Anordnung die halbe Aussage, und ob eine Bindung
 * die richtige ist, sieht man am gezeigten Wert und nicht an einer Guid.
 * Deshalb laufen hier die echten Renderer im echten Raster, gespeist aus
 * `LivePlantBindingProvider`, also aus den echten Werten dieser Anlage. Die
 * Flaeche ist dabei bedienungsblind geschaltet (siehe `.hv-ai-hmi__cell` in
 * der CSS): ein Klick in eine Vorschau darf kein Schaltbefehl an die Anlage
 * werden.
 *
 * WAS WEGGEFALLEN IST, STEHT DANEBEN UND NICHT IN EINEM PROTOKOLL. Der Server
 * verwirft, was er nicht bauen kann: Kacheln ohne aufloesbare Pflichtbindung,
 * Bindungen auf Datenpunkte, die es hier nicht gibt, Konfiguration, mit der
 * der Rumpf ungueltig waere. Ein Vorschlag, der die Haelfte weggelassen hat,
 * sieht ohne diese Angabe aus wie ein vollstaendiger. Sie gehoert an die
 * Stelle, an der der Autor entscheidet.
 *
 * UEBERNEHMEN SPEICHERT NICHT. Angehaengt wird in den Entwurf des Stores,
 * ueber `store.apply`, also rueckgaengig-faehig und ungespeichert. Der Autor
 * soll den Vorschlag ansehen, aendern und dann selbst speichern; ein
 * Vorschlag, der sich selbst speichert, nimmt ihm genau den Schritt weg, an
 * dem er ihn noch ablehnen koennte. Der zweite Weg, ein neues Projekt
 * anlegen, schreibt notgedrungen auf den Server (anders entsteht kein
 * Projekt), aber er ueberschreibt dabei nichts.
 */

import { useMemo, useState } from 'preact/hooks';

import { AiTaskChrome, type AiTaskStage } from './AiTaskChrome';
import {
  appendSuggestion,
  freeProjectName,
  type AppendSuggestionResult,
} from './hmiSuggestionMerge';
import { ApiError } from '../../api/client';
import { AiDashboard } from '../../icons/HvacIcons';
import { LivePlantBindingProvider } from '../../hmi/PointBindingProvider';
import { WidgetHost } from '../../hmi/WidgetHost';
import {
  createHmiProject,
  parseProjectBody,
  suggestHmiScreen,
  type HmiProjectDetail,
  type HmiSuggestion,
} from '../../hmi/projectApi';
import type {
  HmiProject,
  HmiScreen,
  WidgetDefinition,
  WidgetPlacement,
} from '../../hmi/widgets';
import './AiHmiModal.css';
import { BRAND } from '../../brand/brand';

/** Der Weg, auf dem der Vorschlag ins Projekt kommt. */
type TakeOver = 'append' | 'create';

interface Props {
  plantId: string;
  /**
   * Das Projekt, das der Editor gerade offen hat, oder null.
   *
   * Null heisst: diese Anlage hat noch kein Projekt (oder es liess sich nicht
   * laden). Dann gibt es nur den Weg "anlegen", und die Wahl entfaellt.
   */
  openProject: { name: string; project: HmiProject } | null;
  /** Die Projektnamen dieser Anlage. Nur fuer die Vorbelegung des Namens. */
  existingProjectNames: readonly string[];
  /**
   * Anhaengen an den offenen Entwurf.
   *
   * Der Aufrufer legt das Ergebnis ueber `store.apply` hin und schaltet auf
   * das neue Bild. Hier wird bewusst NICHT gespeichert.
   */
  onAppend(result: AppendSuggestionResult): void;
  /** Ein neues Projekt ist entstanden und liegt auf dem Server. */
  onCreated(detail: HmiProjectDetail): void;
  onClose(): void;
}

const PROGRESS_MESSAGES = [
  `${BRAND.aiName} liest die Datenpunkte der Anlage…`,
  'Objekte werden erkannt (Heizkreise, Waermepumpen, Speicher, …)',
  'Passende Kacheln werden gewaehlt und gebunden…',
  'Die Kacheln werden auf das Zwoelfspaltenraster gelegt…',
  'Der Rumpf wird geprueft, mit demselben Pruefer wie beim Speichern…',
];

/** Das eine Ziel, fuer das ein Vorschlag eine Anordnung mitbringt. Die drei
 *  uebrigen fehlen mit Absicht, siehe `SuggestedProjectBodyBuilder`. */
const PREVIEW_TARGET = 'desktopResponsive';

export function AiHmiModal({
  plantId,
  openProject,
  existingProjectNames,
  onAppend,
  onCreated,
  onClose,
}: Props) {
  const [hint, setHint] = useState('');
  const [stage, setStage] = useState<AiTaskStage>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<HmiSuggestion | null>(null);
  const [projectName, setProjectName] = useState('');
  // Vorbelegt ist Anhaengen, wo es ein offenes Projekt gibt. Begruendung:
  // dieser Weg ist der einzige, der nichts auf dem Server anfasst. Er endet in
  // einem ungespeicherten Entwurf, den der Autor ansehen, aendern und
  // verwerfen kann; die Rueckgaengig-Kette holt ihn mit einem Klick wieder
  // weg. Anlegen dagegen erzeugt ein Projekt, das danach in der Liste steht,
  // auch wenn der Vorschlag nichts taugte, und muesste von Hand wieder
  // geloescht werden. Zwischen zwei Vorbelegungen gehoert die harmlosere nach
  // vorn. Dass das offene Projekt leer oder voll ist, aendert daran nichts:
  // beim leeren ist das Anhaengen ohnehin das Fuellen, beim vollen ist es ein
  // weiteres Bild neben den bestehenden.
  const [mode, setMode] = useState<TakeOver>(openProject ? 'append' : 'create');

  // Der Rumpf wird genau einmal ausgepackt, und zwar hier: er treibt sowohl
  // die Vorschau als auch das Anhaengen. Zwei Auspackstellen waeren zwei
  // Gelegenheiten, Verschiedenes zu sehen und zu uebernehmen.
  const parsed = useMemo(
    () => (suggestion ? parseProjectBody(suggestion.bodyJson) : null),
    [suggestion],
  );

  const run = async () => {
    setStage('loading');
    setErrorMsg(null);
    try {
      const res = await suggestHmiScreen(plantId, hint.trim() || null);
      setSuggestion(res);
      setProjectName(freeProjectName(res.screenName, existingProjectNames));
      setStage('result');
    } catch (err) {
      setErrorMsg(explain(err));
      setStage('error');
    }
  };

  const apply = async () => {
    if (!suggestion || !parsed) return;
    if (!parsed.ok) {
      setErrorMsg(`Der Vorschlag laesst sich nicht lesen: ${parsed.error}`);
      return;
    }

    if (mode === 'append') {
      if (!openProject) return;
      // Reine Rechnung, kein Netz: das Ergebnis geht an den Aufrufer und von
      // dort in den Entwurf. Das Ausweichen bei Id-Kollisionen steckt in
      // `appendSuggestion` und ist dort getestet.
      onAppend(appendSuggestion(openProject.project, {
        widgets: parsed.widgets,
        screens: parsed.screens,
      }));
      onClose();
      return;
    }

    setStage('applying');
    setErrorMsg(null);
    try {
      const created = await createHmiProject(plantId, {
        name: projectName.trim() || suggestion.screenName,
        description: suggestion.screenDescription ?? undefined,
        // Unveraendert. Dieser Rumpf ist auf dem Server schon durch denselben
        // Pruefer gelaufen, den auch das Speichern benutzt; etwas daran zu
        // drehen hiesse, diese Zusage wegzuwerfen.
        bodyJson: suggestion.bodyJson,
      });
      onCreated(created);
      onClose();
    } catch (err) {
      setErrorMsg(explain(err));
      setStage('result');
    }
  };

  const droppedCount = suggestion
    ? suggestion.droppedWidgets.length
      + suggestion.droppedBindings.length
      + suggestion.droppedConfig.length
    : 0;
  const canApply = suggestion !== null
    && parsed !== null
    && parsed.ok
    && (mode === 'create'
      ? projectName.trim().length > 0
      : openProject !== null);

  // Kein `chatContext`, und das mit Absicht: die Konsole in `AiConsole` kennt
  // genau drei Betriebsarten (schema, units, dashboard), jede mit eigenem
  // Begruessungstext. Eine vierte anzumelden hiesse, an einer geteilten Datei
  // zu schreiben, die zu dieser Aufgabe nicht gehoert. Der Freitext-Hinweis
  // leistet hier dasselbe.
  return (
    <AiTaskChrome
      title={`${BRAND.aiName} · HMI-Bild vorschlagen`}
      description={
        'Die KI sieht sich den Datenpunktbestand dieser Anlage an und baut daraus ein '
        + 'Bild fuer den Desktop. Was sie vorschlaegt, siehst du unten mit echten Werten, '
        + 'bevor du es nimmst.'
      }
      hint={hint}
      onHintChange={setHint}
      hintPlaceholder="z.B.: 'nur das Erdgeschoss', 'Waermepumpe und Warmwasser gross, Heizkreise klein', 'ein Bild fuer den Hausmeister'"
      stage={stage}
      errorMessage={errorMsg}
      progressMessages={PROGRESS_MESSAGES}
      onClose={onClose}
      hero={<span class="hv-ai-hmi__hero"><AiDashboard size={72} style={{ color: 'var(--ai-accent)' }} /></span>}
      footer={
        stage === 'result' ? (
          <>
            <span class="muted hv-ai-hmi__tally">
              {suggestion?.widgets.length ?? 0} Kacheln
              {droppedCount > 0 ? `, ${droppedCount} Punkte weggefallen` : ''}
            </span>
            <button type="button" class="hv-btn" onClick={onClose}>Abbrechen</button>
            <button type="button" class="hv-btn hv-btn--primary" onClick={apply} disabled={!canApply}>
              {mode === 'append' ? 'In das offene Projekt uebernehmen' : 'Als neues Projekt anlegen'}
            </button>
          </>
        ) : stage === 'applying' ? (
          <button type="button" class="hv-btn" disabled>Projekt wird angelegt…</button>
        ) : (
          <>
            <div style={{ flex: 1 }} />
            <button type="button" class="hv-btn" onClick={onClose} disabled={stage === 'loading'}>
              Abbrechen
            </button>
            <button type="button" class="hv-btn hv-btn--primary" onClick={run}
                    disabled={stage === 'loading'}>
              {stage === 'loading' ? `${BRAND.aiName} arbeitet…` : 'Vorschlagen'}
            </button>
          </>
        )
      }
    >
      {suggestion && (
        <>
          {/* Ein Fehler beim Uebernehmen darf die Vorschau nicht wegnehmen:
              der Autor hat sie gerade gelesen, und der naechste Versuch
              beginnt mit derselben Entscheidung. */}
          {errorMsg && <p class="hv-ai-modal__error">{errorMsg}</p>}
          {suggestion.rationale && (
            <p class="hv-ai-modal__rationale">{suggestion.rationale}</p>
          )}

          {parsed?.ok ? (
            <SuggestionPreview
              plantId={plantId}
              widgets={parsed.widgets}
              screens={parsed.screens}
            />
          ) : (
            <p class="hv-ai-modal__error">
              Der Server hat einen Rumpf geliefert, den dieser Editor nicht lesen kann:
              {' '}{parsed?.ok === false ? parsed.error : ''}
            </p>
          )}

          <DroppedPanel suggestion={suggestion} />

          <TakeOverChooser
            mode={mode}
            onMode={setMode}
            openProject={openProject}
            projectName={projectName}
            onProjectName={setProjectName}
          />
        </>
      )}
    </AiTaskChrome>
  );
}

// ------------------------------------------------------------- Vorschau

/**
 * Der Vorschlag als Bild, mit den echten Renderern und echten Werten.
 *
 * Eigener `LivePlantBindingProvider` und nicht der des Editors: das Modal
 * haengt ueber `createPortal` am `document.body`, und obwohl Preact den
 * Context durch ein Portal hindurch weiterreicht, kann dieses Modal auch von
 * einer Stelle geoeffnet werden, an der es noch kein Projekt und damit keinen
 * Provider gibt (der Editor stellt ihn erst um die Bearbeitungsflaeche). Ein
 * eigener Provider kostet nichts: das Hub-Abo im Signal-Store zaehlt mit, und
 * der einmalige Werte-Abruf ist gegen Doppelung abgesichert
 * (`tryClaimSnapshotFetch`).
 *
 * Fehlt einem Widget die Platzierung, laeuft es im Auto-Flow mit. Sichtbar
 * und an falscher Stelle ist besser als unsichtbar: unsichtbar waere von
 * "hat die KI gar nicht vorgeschlagen" nicht zu unterscheiden.
 */
function SuggestionPreview({
  plantId,
  widgets,
  screens,
}: {
  plantId: string;
  widgets: readonly WidgetDefinition[];
  screens: readonly HmiScreen[];
}) {
  const byId = useMemo(
    () => new Map(widgets.map((w) => [w.id, w] as const)),
    [widgets],
  );

  if (screens.length === 0) {
    return <p class="hv-ai-hmi__empty">Der Vorschlag enthaelt kein Bild.</p>;
  }

  return (
    <LivePlantBindingProvider plantId={plantId}>
      {screens.map((screen) => {
        const layout = screen.layouts.find((l) => l.target === PREVIEW_TARGET)
          ?? screen.layouts[0];
        const placements: Record<string, WidgetPlacement> = layout?.placements ?? {};
        const columns = layout?.canvasSize?.columns ?? 12;
        return (
          <section key={screen.id} class="hv-ai-hmi__screen">
            <h3 class="hv-ai-hmi__screen-title">
              {screen.label}
              <span class="muted"> · {screen.widgetIds.length} Kacheln · Desktop</span>
            </h3>
            {screen.widgetIds.length === 0 ? (
              <p class="hv-ai-hmi__empty">
                Ein benanntes, leeres Bild. Der Server hat keine Kachel bauen koennen;
                was daran lag, steht unten.
              </p>
            ) : (
              <div
                class="hv-ai-hmi__grid"
                style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
              >
                {screen.widgetIds.map((widgetId) => {
                  const widget = byId.get(widgetId);
                  if (!widget) return null;
                  const placement = placements[widgetId];
                  const style = placement
                    ? {
                        gridColumn: `${placement.col + 1} / span ${Math.max(1, placement.colSpan)}`,
                        gridRow: `${placement.row + 1} / span ${Math.max(1, placement.rowSpan)}`,
                      }
                    : { gridColumn: 'span 3' };
                  return (
                    <div key={widgetId} class="hv-ai-hmi__cell" style={style}>
                      <WidgetHost widget={widget} />
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        );
      })}
    </LivePlantBindingProvider>
  );
}

// ----------------------------------------------------- Was weggefallen ist

/**
 * Das Weggefallene, aufgeklappt.
 *
 * Aufgeklappt und nicht zugeklappt: es ist Teil der Entscheidung und nicht
 * ihr Anhang. Wer zwoelf Heizkreise hat und acht Kacheln bekommt, muss das
 * sehen, bevor er uebernimmt, nicht danach.
 */
function DroppedPanel({ suggestion }: { suggestion: HmiSuggestion }) {
  const { droppedWidgets, droppedBindings, droppedConfig } = suggestion;
  const total = droppedWidgets.length + droppedBindings.length + droppedConfig.length;

  if (total === 0) {
    return (
      <p class="hv-ai-hmi__complete">
        Nichts weggefallen: jede vorgeschlagene Kachel steht im Bild, und jede Bindung
        zeigt auf einen Datenpunkt dieser Anlage.
      </p>
    );
  }

  return (
    <section class="hv-ai-hmi__dropped">
      <h3 class="hv-ai-hmi__dropped-title">
        {total} Punkte sind weggefallen
      </h3>
      {droppedWidgets.length > 0 && (
        <>
          <h4>Kacheln ({droppedWidgets.length})</h4>
          <ul>
            {droppedWidgets.map((w, i) => (
              <li key={i}>
                <span class="hv-ai-hmi__kind">{w.kind}</span>
                {w.label && <strong> {w.label}</strong>}
                <span class="muted"> · {w.reason}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      {droppedBindings.length > 0 && (
        <>
          <h4>Bindungen ({droppedBindings.length})</h4>
          <ul>
            {droppedBindings.map((b, i) => <li key={i} class="mono">{b}</li>)}
          </ul>
        </>
      )}
      {droppedConfig.length > 0 && (
        <>
          <h4>Konfiguration ({droppedConfig.length})</h4>
          <ul>
            {droppedConfig.map((c, i) => <li key={i} class="mono">{c}</li>)}
          </ul>
        </>
      )}
      <p class="muted hv-ai-hmi__dropped-note">
        Weggefallen heisst: der Server hat es nicht bauen koennen, meist weil ein
        Pflicht-Datenpunkt auf dieser Anlage fehlt. Das Bild laesst sich trotzdem
        uebernehmen; die fehlenden Kacheln legst du danach von Hand dazu.
      </p>
    </section>
  );
}

// --------------------------------------------------------- Uebernahme-Weg

function TakeOverChooser({
  mode,
  onMode,
  openProject,
  projectName,
  onProjectName,
}: {
  mode: TakeOver;
  onMode: (m: TakeOver) => void;
  openProject: { name: string } | null;
  projectName: string;
  onProjectName: (v: string) => void;
}) {
  return (
    <fieldset class="hv-ai-hmi__takeover">
      <legend>Wohin damit?</legend>

      <label class={`hv-ai-hmi__choice${mode === 'append' ? ' is-active' : ''}`}>
        <input
          type="radio"
          name="hv-ai-hmi-takeover"
          checked={mode === 'append'}
          disabled={!openProject}
          onChange={() => onMode('append')}
        />
        <span>
          <strong>An das offene Projekt anhaengen</strong>
          {openProject
            ? (
              <span class="muted">
                {' '}Das Bild kommt als weiteres Bild in „{openProject.name}“.
                {' '}<strong>Gespeichert wird dabei nichts</strong>: es steht danach als
                {' '}nicht gespeicherter Entwurf da, Rueckgaengig nimmt es wieder weg,
                {' '}speichern tust du selbst.
              </span>
            )
            : <span class="muted"> Geht nicht: es ist kein Projekt offen.</span>}
        </span>
      </label>

      <label class={`hv-ai-hmi__choice${mode === 'create' ? ' is-active' : ''}`}>
        <input
          type="radio"
          name="hv-ai-hmi-takeover"
          checked={mode === 'create'}
          onChange={() => onMode('create')}
        />
        <span>
          <strong>Als neues Projekt anlegen</strong>
          <span class="muted">
            {' '}Der Weg, wenn du mit dem Vorschlag etwas Eigenes beginnen willst.
            {' '}Das Projekt entsteht dabei auf dem Server; bestehende Projekte
            {' '}bleiben unangetastet.
          </span>
          <input
            type="text"
            class="hv-ai-hmi__name"
            value={projectName}
            disabled={mode !== 'create'}
            onInput={(e) => onProjectName((e.target as HTMLInputElement).value)}
            aria-label="Name des neuen Projekts"
            placeholder="Name des neuen Projekts"
          />
        </span>
      </label>
    </fieldset>
  );
}

// ------------------------------------------------------------------ Helfer

/**
 * Fehler in einen Satz, der sagt, was zu tun ist.
 *
 * Der Unterschied zwischen 500 und allem anderen ist hier der Punkt. HTTP 500
 * heisst nicht "die KI hat nichts gefunden", sondern "der Server konnte aus
 * der Antwort des Modells kein speicherbares Projekt bauen". Das erste waere
 * eine Aussage ueber die Anlage, das zweite eine ueber den Lauf: beim ersten
 * hilft ein anderer Hinweis nichts, beim zweiten oft ein zweiter Versuch.
 */
function explain(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; detail?: string; title?: string } | undefined;
    const serverSays = body?.error ?? body?.detail ?? body?.title ?? null;
    switch (err.status) {
      case 500:
        return 'Der Server konnte aus dem Vorschlag kein speicherbares Projekt bauen'
          + (serverSays ? `: ${serverSays}` : '.')
          + ' Das ist etwas anderes als "nichts gefunden": das Modell hat geantwortet,'
          + ' der Rumpf daraus war nicht gueltig. Ein zweiter Lauf, gern mit einem'
          + ' engeren Hinweis, kann durchgehen.';
      case 503:
        return `${BRAND.aiName} ist auf diesem Server nicht eingerichtet`
          + (serverSays ? `: ${serverSays}` : '.');
      case 502:
        return 'Das Modell hat nicht geantwortet'
          + (serverSays ? `: ${serverSays}` : '.')
          + ' Der Vorschlag ist nicht zustande gekommen, an der Anlage liegt es nicht.';
      case 404:
        return 'Diese Anlage gibt es nicht (mehr).';
      default:
        return serverSays ?? `Fehler ${err.status}`;
    }
  }
  if (err instanceof Error) return err.message;
  return 'Unbekannter Fehler.';
}
